import { Injectable, inject, signal } from '@angular/core';
import { distinctUntilChanged, skip } from 'rxjs';
import { NebulaNavigationService } from 'nebula-ui-kit';
import { TranslationService, leerContextoDeProducto } from '@nebula-shared/data-access';
import { SidebarService } from './sidebar.service';
import { SidebarMenuItem } from '../../../domain/src/lib/sidebar.model';
import {
  detectLayoutModule,
  isAtOpenTabLimit,
  preserveLayoutModuleRoute,
} from '../../../utils/src/lib/tab-limit-util';

/** Opcion del menu aplanada, indexable para la busqueda global del header. */
export interface MenuSearchOption {
  label: string;
  route: string;
  icon: string;
  group: string;
  relevance?: number;
}

/** Resultado de abrir una opcion desde la busqueda global. */
export type OpenResult = 'opened' | 'activated' | 'limit';

/**
 * Busqueda global del header (estilo command-palette de Electo): filtra las OPCIONES del menu
 * a las que el usuario tiene acceso por rol y, al elegir una, la abre como pestaña reutilizando
 * el mismo mecanismo del sidebar ({@link NebulaNavigationService#addTabFromMenu}) y respetando el
 * limite de pestañas por modulo ({@link MAX_OPEN_TABS}). Es 100% cliente: no consulta backend.
 */
@Injectable({ providedIn: 'root' })
export class GlobalMenuSearchService {
  private readonly sidebarService = inject(SidebarService);
  private readonly navService = inject(NebulaNavigationService);
  private readonly translation = inject(TranslationService);

  /** Cache de opciones planas del menu del usuario (se carga al primer uso). */
  private readonly options = signal<MenuSearchOption[]>([]);

  /**
   * Las opciones indexadas del producto activo. Cambia cuando llega el menu, de modo que quien
   * espera una opcion que aun no se habia cargado puede volver a buscarla.
   */
  readonly opciones = this.options.asReadonly();
  private loaded = false;

  /** Producto con el que se construyo el indice vigente; si cambia, el indice se rehace. */
  private indexedProductId: string | null = null;

  /**
   * Idioma con el que se construyo el indice vigente. Las etiquetas llegan ya traducidas del
   * menu, asi que al cambiar de idioma el indice se rehace: si no, el buscador seguia mostrando
   * y comparando los nombres en español (Mantis #423).
   */
  private indexedLang: string | null = null;

  constructor() {
    // Se rehace al CAMBIAR el idioma, no al buscar: el menu llega por HTTP, y si se esperaba a la
    // primera busqueda esta se resolvia con el indice anterior (Mantis #423).
    this.translation.currentLang$
      .pipe(skip(1), distinctUntilChanged())
      .subscribe(() => this.alCambiarIdioma());
  }

  /** Con un indice ya armado, lo rehace en el idioma vigente; sin indice no hay nada que rehacer. */
  alCambiarIdioma(): void {
    if (this.loaded) this.loadOptions();
  }

  /**
   * Carga y cachea las opciones planas del menu del usuario, POR PRODUCTO.
   *
   * <p>El indice se cacheaba una sola vez y no se invalidaba nunca, en un servicio de ambito
   * raiz que solo se invoca desde el `ngOnInit` del encabezado. Como el encabezado no se vuelve
   * a crear al conmutar de producto, el buscador seguia ofreciendo las opciones del producto
   * anterior: destinos que no pertenecen al producto activo y que, al abrirse, quedan en blanco.
   * El menu lateral si se rehacia, porque escucha el cambio de producto; el buscador no tenia
   * equivalente (Mantis #285).</p>
   *
   * <p>El filtro por producto no es del buscador: lo aplica {@link SidebarService#getMenu}, que
   * lee el producto vigente al suscribirse. Basta entonces con volver a pedirlo cuando el
   * producto cambia, sin duplicar aqui el detector que ya vive en el menu lateral.</p>
   */
  loadOptions(): void {
    const productId = leerContextoDeProducto('selectedProductId');
    const lang = this.translation.currentLang;
    if (this.loaded && productId === this.indexedProductId && lang === this.indexedLang) return;

    this.loaded = true;
    this.indexedProductId = productId;
    this.indexedLang = lang;

    this.sidebarService.getMenu().subscribe({
      next: (tree) =>
        queueMicrotask(() => {
          // Respuesta de un producto que ya se conmuto: se descarta, o pisaria el indice bueno.
          if (this.indexedProductId !== productId || this.indexedLang !== lang) return;
          this.options.set(this.flatten(tree));
        }),
      error: () => {
        // Se sueltan los dos: dejar solo `loaded` en falso conservaba el producto indexado y
        // el indice vacio, de modo que el reintento no llegaba a rehacerse.
        this.loaded = false;
        this.indexedProductId = null;
        this.indexedLang = null;
      },
    });
  }

  /** Aplana el arbol del menu a hojas navegables, conservando el grupo como categoria. */
  private flatten(tree: SidebarMenuItem[]): MenuSearchOption[] {
    const out: MenuSearchOption[] = [];
    (tree || []).forEach((group) => {
      if (group.children && group.children.length > 0) {
        group.children.forEach((opt) => {
          if (opt.route) {
            out.push({ label: opt.label, route: opt.route, icon: opt.icon, group: group.label });
          }
        });
      } else if (group.route) {
        out.push({ label: group.label, route: group.route, icon: group.icon, group: '' });
      }
    });
    return out;
  }

  /**
   * Busca opciones del menu por relevancia (titulo y categoria). Devuelve el top 15.
   *
   * <p>Comprueba el indice ANTES de descartar consultas cortas: es lo que hace que el cambio de
   * producto se detecte cuando el usuario busca, sin depender de que el encabezado se recree.</p>
   */
  search(query: string): MenuSearchOption[] {
    this.loadOptions();
    const q = this.normalize(query);
    if (q.length < 2) return [];
    return this.options()
      .map((o) => ({ ...o, relevance: this.relevance(o, q) }))
      .filter((o) => (o.relevance ?? 0) > 0)
      .sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0))
      .slice(0, 15);
  }

  private relevance(o: MenuSearchOption, q: string): number {
    const title = this.normalize(o.label);
    const group = this.normalize(o.group);
    let score = 0;
    if (title === q) score += 100;
    if (title.startsWith(q)) score += 50;
    if (title.includes(q)) score += 30;
    if (group.includes(q)) score += 20;
    return score;
  }

  /**
   * La opcion del menu del producto activo a la que pertenece una url: la de ruta mas larga que
   * sea prefijo de ella, por segmentos completos. Asi una url de detalle
   * (`/layout/accounting/terceros/view/7`) se reconoce como de su opcion (`/terceros`).
   *
   * <p>Se usa para abrir la pestaña de la pantalla a la que se llega sin pestaña, por ejemplo con
   * «atras» del navegador despues de pasar por la seleccion de productos (Mantis #439).</p>
   *
   * @param url la url a la que se llego.
   * @returns la opcion con su ruta completa `/layout/<modulo>/...`, o undefined si el menu no la
   *   tiene o aun no se ha cargado.
   */
  opcionDeLaRuta(url: string): MenuSearchOption | undefined {
    this.loadOptions();
    // El modulo sale de la url y no de la sesion: al pasar por la seleccion de productos la ruta
    // del modulo se borra, y las rutas relativas del menu se armarian sobre otro modulo. La url se
    // decodifica igual que las del menu (Tesorería llega como «tesorer%C3%ADa»).
    const modulo = detectLayoutModule(url);
    const clave = this.routeKey(preserveLayoutModuleRoute(url, modulo));
    let mejor: MenuSearchOption | undefined;
    let largo = 0;
    for (const opcion of this.options()) {
      const ruta = preserveLayoutModuleRoute(opcion.route, modulo);
      const prefijo = this.routeKey(ruta);
      const contiene = clave === prefijo || clave.startsWith(prefijo + '/');
      if (prefijo && contiene && prefijo.length > largo) {
        mejor = { ...opcion, route: ruta };
        largo = prefijo.length;
      }
    }
    return mejor;
  }

  /**
   * La opcion del menu del producto activo cuya ruta termina en `ruta`, para abrirla en su
   * pestaña desde fuera del menu (la bandeja de tareas, HT_ARQ_013).
   *
   * @param ruta ruta de la opcion dentro del producto, p. ej. `tareas/list`.
   * @returns la opcion, o undefined si el menu no la tiene o aun no se ha cargado.
   */
  optionForRoute(ruta: string): MenuSearchOption | undefined {
    this.loadOptions();
    const fin = '/' + ruta.replace(/^\/+/, '').toLowerCase();
    return this.options().find((o) => this.routeKey(this.normalizeRoute(o.route)).endsWith(fin));
  }

  /**
   * Abre la opcion como pestaña: si ya esta abierta la activa; si no, valida el limite por modulo
   * y la agrega via {@link NebulaNavigationService#addTabFromMenu} (que ademas navega).
   */
  openOption(opt: MenuSearchOption): OpenResult {
    // La ruta del menu puede venir relativa (p. ej. '/tipo-plan/list'); se normaliza a
    // '/layout/<modulo>/...' igual que el sidebar, o no navega (pestaña vacia).
    const route = this.normalizeRoute(opt.route);
    const key = this.routeKey(route);
    const existing = this.navService.tabs().find((t) => t.route && this.routeKey(t.route) === key);
    if (existing?.id) {
      this.navService.selectTab(existing.id);
      return 'activated';
    }

    const module = detectLayoutModule(route);
    if (isAtOpenTabLimit(this.navService.tabs(), module)) {
      return 'limit';
    }

    this.navService.addTabFromMenu({ label: opt.label, icon: opt.icon, route });
    return 'opened';
  }

  /** Clave de ruta normalizada (sin query/hash, minusculas) para comparar pestañas. */
  private routeKey(route: string | null | undefined): string {
    if (!route) return '';
    return route
      .toLowerCase()
      .replace(/tesoreria/g, 'tesorería')
      .split('?')[0]
      .split('#')[0]
      .replace(/\/$/, '');
  }

  /** True si la ruta es la base/dashboard del modulo (no cuenta para el limite). */
  private normalizeRoute(route: string): string {
    return preserveLayoutModuleRoute(
      route,
      leerContextoDeProducto('selectedRoute') || 'accounting',
    );
  }

  private normalize(str: string): string {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
